/**
 * Cart drawer UI controller.
 *
 * Two jobs, both of which have to live outside the drawer because Dawn keeps
 * replacing pieces of it.
 *
 * 1. The stale `is-empty` class
 * -----------------------------
 * `<cart-drawer class="drawer is-empty">` is the PARENT of `#CartDrawer`, and
 * Dawn's `renderContents()` only rewrites the child's innerHTML — it never
 * touches the wrapper's class. So after the first add from an empty cart the
 * class survived, and Dawn's own stylesheet then applied
 * `cart-drawer.is-empty .drawer__header { display: none }`, while `open()`
 * called `trapFocus(.drawer__inner-empty)` on a node that no longer existed and
 * threw. We reconcile the class against what the server actually rendered.
 *
 * 2. Accordion state
 * ------------------
 * `<details open>` is markup, not state, so a shopper's choice was lost on every
 * cart update. It is mirrored to localStorage — but a stored preference must not
 * outrank the server when the server's own default changes. Each module carries
 * `data-cn-auto`, the default Liquid computed for the current cart; when that
 * differs from the value in storage the server wins and the stored preference is
 * refreshed. That is what lets all three modules auto-collapse once the cart
 * reaches three items, while still respecting a shopper who opens one manually.
 *
 * There is deliberately NO open/close animation. The previous version animated
 * `grid-template-rows` and had to hold back the `open` attribute until the
 * transition finished; combined with `cart-gift-progress.js` mutating the drawer
 * (which fires the MutationObserver, which re-applied state) it produced exactly
 * the janky, half-open behaviour this replaces. Native `<details>` toggling is
 * instant and cannot get stuck.
 */
(function () {
  if (window.CartDrawerUILoaded) return;
  window.CartDrawerUILoaded = true;

  var STORE_KEY = 'cn:drawer:modules';
  /* Modules whose open/closed state is always decided by Liquid, never by a
     remembered preference. */
  var ALWAYS_FOLLOW_SERVER = ['flash'];

  /* ------------------------------------------------------------------ *
   * 1. Keep `is-empty` honest
   * ------------------------------------------------------------------ */
  function syncEmptyClass() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    /* The empty block is the source of truth — it is what the server rendered. */
    var isEmpty = !!drawer.querySelector('.drawer__inner-empty');
    drawer.classList.toggle('is-empty', isEmpty);
  }

  /* ------------------------------------------------------------------ *
   * 2. Accordion state
   * ------------------------------------------------------------------ */
  function readState() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORE_KEY));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* Storage unavailable — modules just follow their markup defaults. */
    }
  }

  function remember(module) {
    var name = module.getAttribute('data-cn-module');
    if (!name) return;
    var state = readState();
    state[name] = { open: !!module.open, auto: module.getAttribute('data-cn-auto') || '' };
    writeState(state);
  }

  /**
   * Restore remembered state, once per element instance.
   *
   * Marking each element matters: the MutationObserver fires constantly because
   * `cart-gift-progress.js` rewrites the progress text and bar width inside the
   * drawer. Re-applying state on those ticks fought whatever the shopper was
   * doing. With the marker, state is only applied to markup the server has just
   * handed us.
   */
  function initModules(root) {
    var state = readState();
    var modules = (root || document).querySelectorAll('[data-cn-module]');
    var dirty = false;

    Array.prototype.forEach.call(modules, function (module) {
      var summary = module.querySelector('.cn-module__summary');

      if (module.dataset.cnInit !== 'true') {
        module.dataset.cnInit = 'true';

        var name = module.getAttribute('data-cn-module');
        var auto = module.getAttribute('data-cn-auto') || '';
        var stored = state[name];

        /* The flash offer is time-boxed, so a stale preference must never keep
           it shut: while it is running with fewer than three items in the cart
           it is always expanded. Other modules remember the shopper's choice. */
        if (ALWAYS_FOLLOW_SERVER.indexOf(name) !== -1) {
          stored = null;
        }

        if (stored && typeof stored === 'object' && stored.auto === auto) {
          /* Same cart situation as when they chose — honour the choice. */
          module.open = stored.open === true;
        } else {
          /* New situation (e.g. the cart just crossed three items): the server's
             default wins, and becomes the new baseline. */
          state[name] = { open: !!module.open, auto: auto };
          dirty = true;
        }
      }

      if (summary) summary.setAttribute('aria-expanded', String(!!module.open));
    });

    if (dirty) writeState(state);
  }

  /* `toggle` does not bubble, so listen in the capture phase. */
  document.addEventListener(
    'toggle',
    function (event) {
      var module = event.target;
      if (!module || !module.hasAttribute || !module.hasAttribute('data-cn-module')) return;

      var summary = module.querySelector('.cn-module__summary');
      if (summary) summary.setAttribute('aria-expanded', String(!!module.open));
      remember(module);
    },
    true,
  );

  /* ------------------------------------------------------------------ *
   * Flash offer expiry
   * ------------------------------------------------------------------ *
   * When the flash module removes itself the drawer loses a block. Open the
   * remaining promos so the space is used rather than left as a gap, and record
   * the new state so the next re-render keeps them open.
   */
  document.addEventListener('cn:flash-expired', function () {
    var state = readState();
    ['gift', 'upsell'].forEach(function (name) {
      var module = document.querySelector('#CartDrawer [data-cn-module="' + name + '"]');
      if (!module || module.hidden) return;
      module.open = true;
      state[name] = { open: true, auto: module.getAttribute('data-cn-auto') || '' };
      var summary = module.querySelector('.cn-module__summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');
    });
    writeState(state);
  });

  /* ------------------------------------------------------------------ *
   * Wire up, and re-wire after every drawer re-render
   * ------------------------------------------------------------------ */
  function refresh() {
    var drawer = document.getElementById('CartDrawer');
    syncEmptyClass();
    initModules(drawer || document);
  }

  function observe() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;

    refresh();

    var scheduled = false;
    var observer = new MutationObserver(function () {
      /* Coalesce the burst of mutations a single innerHTML swap produces. */
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        refresh();
      });
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
